import { type RefObject, useEffect, useRef } from 'react';

type EventMap = {
    'poi-click': any[] | undefined;
    'navi-complete': any[] | undefined;
    'marker-click': any | undefined;
    'floor-changed': any | undefined;
    'zoom-changed'?: any;
    'void-click'?: any;
    'on-mouse-click'?: any;
    'on-mouse-move'?: any;
};

type EventCallbacks = {
    onPoiClick?: (data: any[]) => void;
    onNavigationComplete?: (data: any[]) => void;
    onMarkerClick?: (data: any) => void;
    onFloorChanged?: (data: any) => void;
    onZoomChanged?: (data: any) => void;
    onVoidClick?: (data: any) => void;
    onMouseClick?: (data: any) => void;
    onMouseMove?: (data: any) => void;
};

type Props<T extends HTMLElement> = {
    mapContainer?: RefObject<T | null>;
};

type HybridProps<T extends HTMLElement> = Props<T> & EventCallbacks;

export const useMapEvents = <T extends HTMLElement>({
    mapContainer,
    onPoiClick,
    onNavigationComplete,
    onMarkerClick,
    onFloorChanged,
    onZoomChanged,
    onVoidClick,
    onMouseClick,
    onMouseMove,
}: HybridProps<T>) => {
    // Store callbacks in refs to avoid re-subscribing on every render
    const callbacksRef = useRef<EventCallbacks>({});
    callbacksRef.current = {
        onPoiClick,
        onNavigationComplete,
        onMarkerClick,
        onFloorChanged,
        onZoomChanged,
        onVoidClick,
        onMouseClick,
        onMouseMove,
    };

    useEffect(() => {
        if (!mapContainer?.current) return;

        const containerElement = mapContainer.current;

        const eventMapping: { eventName: keyof EventMap; key: keyof EventCallbacks }[] = [
            { eventName: 'poi-click', key: 'onPoiClick' },
            { eventName: 'navi-complete', key: 'onNavigationComplete' },
            { eventName: 'marker-click', key: 'onMarkerClick' },
            { eventName: 'floor-changed', key: 'onFloorChanged' },
            { eventName: 'zoom-changed', key: 'onZoomChanged' },
            { eventName: 'void-click', key: 'onVoidClick' },
            { eventName: 'on-mouse-click', key: 'onMouseClick' },
            { eventName: 'on-mouse-move', key: 'onMouseMove' },
        ];

        const handlers: { eventName: string; handler: EventListener }[] = [];

        eventMapping.forEach(({ eventName, key }) => {
            const handler = ((e: CustomEvent) => {
                callbacksRef.current[key]?.(e.detail);
            }) as EventListener;
            containerElement.addEventListener(eventName, handler);
            handlers.push({ eventName, handler });
        });

        return () => {
            handlers.forEach(({ eventName, handler }) => {
                containerElement.removeEventListener(eventName, handler);
            });
        };
    }, [mapContainer]);
};
